import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';

import { triggerTracking, trackVirtualPage } from '@zoopla/tracking';
import { Link } from '@zoopla-eng/zds-components/link';
import { Breadcrumbs } from '@zoopla-eng/zds-components/breadcrumbs';
import { Hero } from '@zoopla/landing-pages/hero';
import { AdScripts } from '@zoopla/adverts/dfp';
import { graphQLWrapper } from '@zoopla/apollo';
import { SubNavigation } from '@zoopla/landing-pages/sub-navigation';
import { Head } from '@zoopla/layout/head';
import { FullwidthHeroLayout } from '@zoopla/layout/page';
import { usePageSegmentTracking } from '@zoopla/tracking';
import { useSchema } from '@zoopla/layout/schema';
import { Sprite } from '@zoopla/sprite';
import sprite from './critical.sprite';
import { returnBreadCrumbs } from './utils';
import { Props } from './landing-pages.types';
import { LandingPagesContentMapper } from '@zoopla/landing-pages/content-mapper';
import {
    breadcrumbWrapperStyle,
    contentStyle,
    heroWrapperStyle,
    layoutWrapperStyle,
} from './landing-pages.styles.css';
import { Document } from '@contentful/rich-text-types';

import { TrackedPropertyRelationship } from '../graphql.generated';
import { SearchType } from '@zoopla/landing-pages/types';
import { useSearchBarExperiment } from '@zoopla/listing-search/listing-search-bar';
import { type FindAgentsSearchProps } from './components/find-agents-search/find-agents-search';
import type {
    CarouselState,
    ValuationResultsOptions,
} from '@zoopla/landing-pages/instant-valuation-form-stepper';

/**
 * To avoid landing-page being a large generic bundle we use next/dynamic to
 * split into sensible chunks
 */
const PropertySearch = dynamic(() =>
    import('./components/property-search').then((mod) => mod.PropertySearch)
);
const FindAgentsSearch = dynamic(
    () => import('./components/find-agents-search/find-agents-search')
);
const HousePricesSearchTabs = dynamic(() =>
    import('@zoopla/house-prices/search').then(
        (mod) => mod.HousePricesSearchTabs
    )
);
const LandingPageSearch = dynamic(() =>
    import('@zoopla/landing-pages/search').then((mod) => mod.LandingPageSearch)
);
const InstantValuationFormStepper = dynamic(() =>
    import('@zoopla/landing-pages/instant-valuation-form-stepper').then(
        (mod) => mod.InstantValuationFormStepper
    )
);
const ValuationResultsInterruption = dynamic(() =>
    import('@zoopla/landing-pages/instant-valuation-form-stepper').then(
        (mod) => mod.ValuationResultsInterruption
    )
);
const MortgageCalculator = dynamic(() =>
    import('@zoopla/mortgage-calculator').then((mod) => mod.MortgageCalculator)
);
const RemortgageFigure = dynamic(() =>
    import('./components/remortgage').then((mod) => mod.RemortgageFigure)
);
const RemortgageBannerCombination = dynamic(() =>
    import('./components/remortgage').then(
        (mod) => mod.RemortgageBannerCombination
    )
);
const Carousel = dynamic(() =>
    import('@zoopla/agent-matcher/carousel').then((mod) => mod.Carousel)
);
const CarouselContainer = dynamic(() =>
    import('./components/agent-carousel/agent-carousel').then(
        (mod) => mod.CarouselContainer
    )
);

/**
 * redefine rather than import otherwise we mix static / next/dynamic imports
 * this logic will be removed soon
 */
export const trackInstantValuationVirtualPage = (
    virtualPageIdentifier: string,
    taxonomyOptions?: Record<string, string | null>
) => {
    const TRACKING_PAGE = '/home-values/';
    const { outcode, uprn } = taxonomyOptions || {};
    const propertyRelationship =
        taxonomyOptions?.['propertyRelationship']?.toLowerCase();
    const taxonomy = {
        brand: 'zoopla',
        section: 'home-values',
        page: TRACKING_PAGE,
        ...(outcode ? { outcode } : {}),
        ...(uprn ? { uprn } : {}),
        ...(propertyRelationship ? { propertyRelationship } : {}),
    } as const;
    trackVirtualPage(TRACKING_PAGE, virtualPageIdentifier, taxonomy);
};

const InstantValuationResultCopy = {
    barCopy: {
        title: 'How your valuation affects your remortgage',
        alt: 'Bar chart showing how the average mortgage interest rate decreases with a lower loan-to-value percentage.',
        bodyHeader: (
            <>
                When remortgaging,{' '}
                <Link
                    href="https://www.zoopla.co.uk/discover/buying/understanding-loan-to-value/"
                    target="_blank"
                >
                    loan-to-value
                </Link>{' '}
                (or 'LTV') is really important. Low LTVs usually mean lower
                rates. Expert advice will always ensure you get the best deal.
            </>
        ),
        chartTitle: 'Average interest rate for a 2 year fixed rate mortgage',
        chartBody: 'Rates last updated 30 June 2023',
        sourceUrl:
            'https://www.bankofengland.co.uk/boeapps/database/fromshowcolumns.asp?Travel=NIxSUx&FromSeries=1&ToSeries=50&DAT=RNG&FD=1&FM=Jan&FY=2023&TD=31&TM=Dec&TY=2023&FNY=&CSVF=TT&html.x=126&html.y=24&C=IW&C=NB1&C=EOT&C=U1P&C=U1R&Filter=N',
    },
    lineCopy: {
        title: "What if I don't do anything?",
        alt: 'Line chart showing the average standard variable rate over the past 2 years, and the increase over the last 12 months.',
        bodyHeader:
            "If you don't take action before your deal ends, you'll likely move onto your lender's standard variable rate (SVR)",
        bodyFooter:
            'SVRs have increased a lot over the last 12 months, which could mean a big increase in your mortgage repayments too.',
        chartTitle: 'Average standard variable rate (SVR)',
        sourceUrl:
            'https://www.bankofengland.co.uk/boeapps/database/fromshowcolumns.asp?Travel=NIxAZxSUx&FromSeries=1&ToSeries=50&DAT=RNG&FD=1&FM=Jan&FY=2021&TD=31&TM=Dec&TY=2025&FNY=Y&CSVF=TT&html.x=66&html.y=26&SeriesCodes=IUMTLMV&UsingCodes=Y&Filter=N&title=IUMTLMV&VPD=Y',
    },
};

const getCarouselProperty = (state?: CarouselState) => {
    // If the relationship isn't homeowner then they're either not a homeowner or
    // have not reached the results step. In both cases there is no valid carousel
    // property so we return.
    if (state?.relationship !== TrackedPropertyRelationship.Homeowner) return;

    return state.property;
};

const isValuationPpcSlug = (slug: SearchType) => {
    return (
        slug === SearchType['VAL_PPC_1'] ||
        slug === SearchType['VAL_PPC_2'] ||
        slug === SearchType['VAL_PPC_3']
    );
};

/**
 * @deprecated
 * DO NOT USE THIS FOR ANYTHING OTHER THAN THE VALUATION PPC TEST PAGES.
 * This is an experimental function that converts the Contentful button value
 * into CTA props _ONLY_ for the val ppc hero.
 */
const dangerouslySetValuationPpcProps = (
    button = ''
): FindAgentsSearchProps => {
    let addressSubmitText: string | undefined;
    let postcodeSubmitText: string | undefined;

    // Split the button text we get from Contentful with the specified
    // delimeter. If no delimeter is passed, we treat "button" as the value
    // for the submit button.
    const [cta1, cta2] = button.split('|');

    if (cta2) {
        postcodeSubmitText = cta1;
        addressSubmitText = cta2;
    } else if (cta1) {
        addressSubmitText = cta1;
    }

    return {
        addressSubmitText,
        postcodeSubmitText,
    };
};

const LandingPages = ({
    slug,
    button,
    hero,
    seoInformation: {
        pageTitle,
        metaDescription,
        openGraphImage,
        index,
        follow,
    },
    contentBlock,
    subNavigation,
}: Props) => {
    const canonicalPath = `/${slug}/`;
    usePageSegmentTracking({
        page: canonicalPath,
    });

    const breadcrumbItems = returnBreadCrumbs(slug);
    const showBreadcrumbs = slug !== 'home-values';

    const schema = useSchema(breadcrumbItems, canonicalPath);

    const handleTracking = (category: string, label: string) => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory: category,
            eventAction: 'clickthrough',
            eventLabel: label,
        });
    };

    const [instantValResultsOptions, setInstantValResultsOptions] =
        useState<ValuationResultsOptions | null>(null);
    const [instantValDismissedInterrupt, setInstantValDismissedInterrupt] =
        useState(false);
    const showInstantValuationInterrupt =
        !!instantValResultsOptions?.showInterrupt &&
        !instantValDismissedInterrupt;

    useEffect(() => {
        // If the instantValResultsOptions is reset to null we know that a user
        // has gone back to the start of the form, so we reset the interrupt
        // state so that it can be shown a second time if they are valuing
        // another property.
        if (!instantValResultsOptions) {
            setInstantValDismissedInterrupt(false);
        }
    }, [instantValResultsOptions]);

    // This ought to be done as part of a callback that prevents the results
    // virtual page from showing, but the architecture for this page means that
    // we don't know the two things concurrently. If this experiment is codified
    // we intend to resolve the loading "jump" that we see, and this should be
    // fixed then too.
    useEffect(() => {
        if (showInstantValuationInterrupt) {
            trackInstantValuationVirtualPage('interruptionExperiment');
        }
    }, [showInstantValuationInterrupt]);

    const isUsingSearchBar = useSearchBarExperiment();
    const [carouselState, setCarouselState] = useState<CarouselState>();
    const carouselProperty = getCarouselProperty(carouselState);

    const hideHero = instantValResultsOptions !== null;

    return (
        <>
            <Head
                pageTitle={pageTitle}
                pageDescription={metaDescription}
                canonicalPath={canonicalPath}
                openGraphValues={[
                    {
                        property: 'og:url',
                        content: `https://www.zoopla.co.uk/${slug}/`,
                    },
                    {
                        property: 'og:title',
                        content: pageTitle,
                    },
                    { property: 'og:type', content: 'article' },
                    {
                        property: 'og:image',
                        content: openGraphImage ?? `https:${hero.image.url}`,
                    },
                    {
                        property: 'og:description',
                        content: metaDescription,
                    },
                ]}
                schema={schema}
                index={() => index}
                follow={follow}
            />
            <AdScripts />
            <Sprite content={sprite} />
            <div className={layoutWrapperStyle}>
                <FullwidthHeroLayout
                    heroBackgroundColor="graphicBackgroundDefault"
                    heroContent={
                        <div
                            className={heroWrapperStyle({
                                showInstantValuationInterrupt,
                            })}
                            // This prevents screenreaders from accessing this
                            // whilst keeping it in the DOM to ensure that the
                            // instant val results component isn't unmounted.
                            aria-hidden={
                                showInstantValuationInterrupt
                                    ? 'true'
                                    : undefined
                            }
                        >
                            {showBreadcrumbs && (
                                <nav
                                    className={breadcrumbWrapperStyle}
                                    aria-label="breadcrumb"
                                >
                                    <Breadcrumbs items={breadcrumbItems} />
                                </nav>
                            )}

                            <Hero
                                title={hero.title}
                                image={hero.image.url}
                                slug={slug}
                                showHero={!hideHero}
                                richTextDescription={
                                    hero.description as Document
                                }
                            >
                                {/* Show For Sale/To Rent/New Homes search forms */}
                                {['for-sale', 'to-rent', 'new-homes'].includes(
                                    slug
                                ) &&
                                    (isUsingSearchBar ? (
                                        <PropertySearch slug={slug} />
                                    ) : (
                                        <LandingPageSearch
                                            type={slug}
                                            onSubmit={() =>
                                                handleTracking(
                                                    slug.replace('_', '-'),
                                                    `${slug}_success)`
                                                )
                                            }
                                            onSubmitFail={() =>
                                                handleTracking(
                                                    slug.replace('_', '-'),
                                                    `${slug}_empty)`
                                                )
                                            }
                                            buttonLabel={button}
                                        />
                                    ))}

                                {slug === 'home-values' && (
                                    <InstantValuationFormStepper
                                        button={button}
                                        onCarouselStateChange={setCarouselState}
                                        onResultsChange={
                                            setInstantValResultsOptions
                                        }
                                    />
                                )}

                                {slug === 'house-prices' && (
                                    <HousePricesSearchTabs
                                        parent="landing"
                                        defaultTab="area"
                                        showContent
                                    />
                                )}

                                {slug === 'find-agents' && <FindAgentsSearch />}

                                {isValuationPpcSlug(slug) && (
                                    <FindAgentsSearch
                                        {...dangerouslySetValuationPpcProps(
                                            button
                                        )}
                                    />
                                )}

                                {slug === 'mortgage-calculator' && (
                                    <MortgageCalculator />
                                )}
                            </Hero>

                            {instantValResultsOptions?.situation ===
                                'remortgage' && (
                                <>
                                    <RemortgageFigure
                                        type="bar"
                                        copy={
                                            InstantValuationResultCopy.barCopy
                                        }
                                        contrastBackground
                                    />
                                    <RemortgageFigure
                                        type="line"
                                        copy={
                                            InstantValuationResultCopy.lineCopy
                                        }
                                        reverseRow
                                    />
                                    <RemortgageBannerCombination />
                                </>
                            )}
                        </div>
                    }
                    content={
                        showInstantValuationInterrupt ? (
                            <ValuationResultsInterruption
                                uprn={instantValResultsOptions.uprn}
                                onDismiss={() => {
                                    setInstantValDismissedInterrupt(true);
                                    trackInstantValuationVirtualPage(
                                        'valuationResults',
                                        {
                                            uprn: instantValResultsOptions.uprn,
                                            // These have to be 'unknown' and
                                            // null sadly as we don't pass this
                                            // value up. If we codify this
                                            // experiment, this should be fixed.
                                            outcode: 'unknown',
                                            propertyRelationship: null,
                                        }
                                    );
                                }}
                            />
                        ) : (
                            <div className={contentStyle}>
                                {subNavigation && (
                                    <SubNavigation
                                        subNavTitle={subNavigation.subNavTitle}
                                        subNav={subNavigation.navLinks}
                                    />
                                )}
                                {carouselProperty && (
                                    <CarouselContainer>
                                        <Carousel
                                            property={carouselProperty}
                                            hasCta
                                            baseTaxonomy={{
                                                activity: 'agent_matcher',
                                                section: 'home-values',
                                                page: '/home-values/',
                                                brand: 'zoopla',
                                                path: canonicalPath,
                                            }}
                                        />
                                    </CarouselContainer>
                                )}
                                {LandingPagesContentMapper(contentBlock, slug)}
                            </div>
                        )
                    }
                />
            </div>
        </>
    );
};

const LandingPage = graphQLWrapper(LandingPages);

export default LandingPage;
